import React, { useState } from 'react';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import { useQuery } from 'react-query';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  UserGroupIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  UserIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  CalendarIcon,
  DocumentTextIcon,
  HeartIcon,
  ChatBubbleLeftIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const Users = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebouncedValue(searchTerm.trim());
  const [roleFilter, setRoleFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('');

  // Récupérer tous les utilisateurs publics
  const { data: usersData = { users: [], pagination: {} }, isLoading, error } = useQuery(
    ['public-users', debouncedSearch],
    async () => {
      // Recherche faite par le serveur (résultats bornés), plus sur une liste figée
      const response = await axios.get('/api/users/public', { params: { search: debouncedSearch } });
      return response.data;
    },
    { keepPreviousData: true }
  );

  // Extraire les utilisateurs
  const users = usersData.users || [];

  // Récupérer les pays pour le filtre
  const { data: countries = [] } = useQuery('countries', async () => {
    const response = await axios.get('/api/countries');
    return response.data.countries || [];
  });

  // Filtrer les utilisateurs
  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.company?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRole = !roleFilter || user.role === roleFilter;
    const matchesCountry = !countryFilter || (user.country && user.country === countryFilter);
    
    return matchesSearch && matchesRole && matchesCountry;
  });

  const getRoleColor = (role) => {
    switch (role) {
      case 'entrepreneur':
        return 'bg-green-100 text-green-800';
      case 'investor':
        return 'bg-blue-100 text-blue-800';
      case 'admin':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'entrepreneur':
        return 'Entrepreneur';
      case 'investor':
        return 'Investisseur';
      case 'admin':
        return 'Administrateur';
      default:
        return 'Utilisateur';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des utilisateurs...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="w-16 h-16 text-red-500 mx-auto mb-4" aria-hidden="true" />
          <h2 className="text-xl font-semibold text-text mb-2">Erreur de chargement</h2>
          <p className="text-gray-600">Impossible de charger la liste des utilisateurs</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* En-tête */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <UserGroupIcon className="h-8 w-8 text-primary-500 mr-3" />
            <h1 className="text-3xl font-bold text-text">Utilisateurs</h1>
          </div>
          <p className="text-gray-600">
            Découvrez les entrepreneurs, investisseurs et utilisateurs de notre plateforme
          </p>
        </div>

        {/* Filtres et recherche */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Recherche */}
            <div className="md:col-span-2">
              <div className="relative">
                <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher par nom, email ou entreprise..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Filtre par rôle */}
            <div>
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Tous les rôles</option>
                <option value="entrepreneur">Entrepreneurs</option>
                <option value="investor">Investisseurs</option>
                <option value="utilisateur">Utilisateurs</option>
              </select>
            </div>

            {/* Filtre par pays */}
            <div>
              <select
                value={countryFilter}
                onChange={(e) => setCountryFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Tous les pays</option>
                {countries.map(country => (
                  <option key={country._id} value={country.name}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Statistiques */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center">
              <div className="p-3 bg-green-100 rounded-lg">
                <UserIcon className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total</p>
                <p className="text-2xl font-bold text-text">{users.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 rounded-lg">
                <BuildingOfficeIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Entrepreneurs</p>
                <p className="text-2xl font-bold text-text">
                  {users.filter(u => u.role === 'entrepreneur').length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center">
              <div className="p-3 bg-purple-100 rounded-lg">
                <UserGroupIcon className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Investisseurs</p>
                <p className="text-2xl font-bold text-text">
                  {users.filter(u => u.role === 'investor').length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center">
              <div className="p-3 bg-orange-100 rounded-lg">
                <DocumentTextIcon className="h-6 w-6 text-orange-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Projets actifs</p>
                <p className="text-2xl font-bold text-text">
                  {users.reduce((total, user) => total + (user.stats?.projectsCount || 0), 0)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Liste des utilisateurs */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredUsers.map((user) => (
            <div key={user.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
              <div className="p-6">
                {/* En-tête de la carte */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                      <UserIcon className="h-6 w-6 text-primary-600" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-lg font-semibold text-text">
                        {user.firstName} {user.lastName}
                      </h3>
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getRoleColor(user.role)}`}>
                        {getRoleLabel(user.role)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Informations utilisateur */}
                <div className="space-y-3 mb-4">
                  {user.company && (
                    <div className="flex items-center text-sm text-gray-600">
                      <BuildingOfficeIcon className="h-4 w-4 mr-2" />
                      {user.company}
                    </div>
                  )}
                  
                  {user.position && (
                    <div className="flex items-center text-sm text-gray-600">
                      <UserIcon className="h-4 w-4 mr-2" />
                      {user.position}
                    </div>
                  )}
                  
                  {user.country && (
                    <div className="flex items-center text-sm text-gray-600">
                      <MapPinIcon className="h-4 w-4 mr-2" />
                      {user.country}
                    </div>
                  )}
                  
                  <div className="flex items-center text-sm text-gray-600">
                    <CalendarIcon className="h-4 w-4 mr-2" />
                    Membre depuis {new Date(user.createdAt).toLocaleDateString('fr-FR')}
                  </div>
                </div>

                {/* Bio */}
                {user.bio && (
                  <div className="mb-4">
                    <p className="text-sm text-gray-600 line-clamp-2">
                      {user.bio}
                    </p>
                  </div>
                )}

                {/* Statistiques */}
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="text-center">
                    <div className="flex items-center justify-center mb-1">
                      <DocumentTextIcon className="h-4 w-4 text-primary-500" />
                    </div>
                    <p className="text-xs text-gray-600">Projets</p>
                    <p className="text-sm font-semibold">{user.stats?.projectsCount || 0}</p>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center mb-1">
                      <HeartIcon className="h-4 w-4 text-red-500" />
                    </div>
                    <p className="text-xs text-gray-600">Favoris</p>
                    <p className="text-sm font-semibold">{user.stats?.favoritesCount || 0}</p>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center mb-1">
                      <ChatBubbleLeftIcon className="h-4 w-4 text-blue-500" />
                    </div>
                    <p className="text-xs text-gray-600">Commentaires</p>
                    <p className="text-sm font-semibold">{user.stats?.commentsCount || 0}</p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex space-x-2">
                  <Link
                    to={`/users/${user.id}`}
                    className="flex-1 flex items-center justify-center px-3 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
                  >
                    <EyeIcon className="h-4 w-4 mr-1" />
                    Voir le profil
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Message si aucun résultat */}
        {filteredUsers.length === 0 && (
          <div className="text-center py-12">
            <UserGroupIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun utilisateur trouvé</h3>
            <p className="text-gray-600">
              Essayez de modifier vos critères de recherche ou de filtrage
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Users;








